import { useMemo } from "react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Pencil, Trash2 } from "lucide-react";
import { format } from "date-fns";
import type { Comment } from "@/services/api/comments";

function getCommentAuthorName(author: Comment["author"]): string {
  if (!author) return "Anonymous";
  if (typeof author === "string") return author;
  const name = author.displayName || author.email || "Unknown";
  if (author.isDeleted) return `${name} (deleted user)`;
  return name;
}

interface CommentItemProps {
  comment: Comment;
  isEditing: boolean;
  editValue: string;
  isUpdating: boolean;
  onStartEdit: (comment: Comment) => void;
  onUpdate: () => void;
  onCancelEdit: () => void;
  onDelete: (id: string | number) => void;
  onEditValueChange: (value: string) => void;
}

export function CommentItem({
  comment,
  isEditing,
  editValue,
  isUpdating,
  onStartEdit,
  onUpdate,
  onCancelEdit,
  onDelete,
  onEditValueChange,
}: CommentItemProps) {
  const authorName = useMemo(
    () => getCommentAuthorName(comment.author),
    [comment.author]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onUpdate();
    } else if (e.key === "Escape") {
      onCancelEdit();
    }
  };

  return (
    <div className="group flex flex-col gap-1 rounded-lg bg-grid-50 p-3">
      <div className="flex items-start justify-between">
        <div className="flex flex-col">
          <span className={`text-xs font-semibold ${typeof comment.author === "object" && comment.author?.isDeleted ? "text-ink-400" : "text-ink-900"}`}>
            {authorName}
          </span>
          <span className="text-[10px] text-ink-400">
            {comment.createdAt
              ? format(new Date(comment.createdAt), "dd/MM/yyyy HH:mm")
              : "Just now"}
          </span>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onStartEdit(comment)}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => onDelete(comment.id)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {isEditing ? (
        <div className="mt-2 flex flex-col gap-2">
          <Input
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={onUpdate}
              disabled={!editValue.trim() || isUpdating}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-ink-700 wrap-break-word whitespace-pre-wrap">
          {comment.text}
        </p>
      )}
    </div>
  );
}
